/**
 * ExchangeRateRow.tsx
 * One editable row in the current-exchange-rate table (Admin → Exchange Rates).
 *
 * Renders a single currency's current rate-to-CAD with an inline editor and a
 * Save action. The CAD base row is rendered separately by the page (read-only),
 * so this component always represents a non-CAD currency.
 *
 * Direction: rateToCAD = CAD per 1 unit of the currency, so the relationship
 * label reads "1 USD = 1.4100 CAD".
 */

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, Save } from '@mui/icons-material';
import { fxRateLabel } from '../../../utils/quoteCurrency';
import { formatUpdatedAt } from './exchangeRateConstants';
import type { CurrentExchangeRate } from '../../../services/scheduleExchangeRates';

interface ExchangeRateRowProps {
  row: CurrentExchangeRate;
  /** Current draft text for the rate input (controlled by the page). */
  draft: string;
  /** True when the draft differs from the saved value and is a valid number. */
  dirty: boolean;
  /** True while this row's Save request is in flight. */
  saving: boolean;
  /** True when the user may edit/save/remove (admin+). */
  canEdit: boolean;
  /** True when this currency is a default and so cannot be removed. */
  isDefault: boolean;
  onDraftChange: (value: string) => void;
  onSave: () => void;
  onRemove: () => void;
}

export const ExchangeRateRow = ({
  row,
  draft,
  dirty,
  saving,
  canEdit,
  isDefault,
  onDraftChange,
  onSave,
  onRemove,
}: ExchangeRateRowProps) => {
  const hasRate = row.rateToCAD != null;
  const relationship = hasRate ? fxRateLabel(row.currency, row.rateToCAD) : null;

  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 600 }}>{row.currency}</TableCell>

      <TableCell sx={{ width: 220 }}>
        <TextField
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canEdit && dirty && !saving) {
              e.preventDefault();
              onSave();
            }
          }}
          type="number"
          size="small"
          fullWidth
          disabled={!canEdit || saving}
          placeholder="Not set"
          inputProps={{ step: '0.0001', min: '0' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">1 {row.currency} =</InputAdornment>
            ),
            endAdornment: <InputAdornment position="end">CAD</InputAdornment>,
          }}
        />
      </TableCell>

      <TableCell>
        {relationship ? (
          <Typography variant="body2">{relationship}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No rate set yet
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatUpdatedAt(row.updatedAt)}
        </Typography>
      </TableCell>

      <TableCell align="right">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Save fontSize="small" />}
            onClick={onSave}
            disabled={!canEdit || !dirty || saving}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {!isDefault && (
            <Tooltip title={canEdit ? 'Remove currency' : 'Requires Lab Admin'}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={onRemove}
                  disabled={!canEdit || saving}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};
